import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { AnalyticsDailyRow } from "@/lib/api";

interface DailyChartProps {
  rows: AnalyticsDailyRow[];
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function DailyChart({ rows }: DailyChartProps) {
  const data = rows.map((r) => ({
    date: shortDate(r.date),
    clicks: r.clicks,
    impressions: r.impressions,
  }));

  return (
    <div className="surface p-4 relative overflow-hidden">
      {/* Corner accents */}
      <span className="absolute top-3 left-3 w-1.5 h-1.5 rounded-full bg-primary animate-pulse-led" />

      <div className="flex items-center justify-between mb-3">
        <p className="path-chip">daily trend</p>
        <div className="flex items-center gap-4 text-xs font-mono">
          <span className="flex items-center gap-1.5 text-muted">
            <span className="w-2 h-2 rounded-full bg-primary shadow-glow" /> impressions
          </span>
          <span className="flex items-center gap-1.5 text-muted">
            <span className="w-2 h-2 rounded-full bg-success shadow-glow-success" /> clicks
          </span>
        </div>
      </div>

      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="imprFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="rgb(0 212 255)"  stopOpacity={0.45} />
                <stop offset="45%"  stopColor="rgb(168 85 247)" stopOpacity={0.18} />
                <stop offset="100%" stopColor="rgb(0 212 255)"  stopOpacity={0} />
              </linearGradient>
              <linearGradient id="imprStroke" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%"   stopColor="rgb(0 212 255)" />
                <stop offset="100%" stopColor="rgb(168 85 247)" />
              </linearGradient>
              <filter id="glowCyan" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2.2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="glowGreen" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <CartesianGrid stroke="rgba(168, 85, 247, 0.06)" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fill: "rgba(240, 240, 255, 0.4)", fontSize: 10, fontFamily: "monospace" }}
              axisLine={{ stroke: "rgba(168, 85, 247, 0.15)" }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={30}
            />
            <YAxis
              yAxisId="left"
              tick={{ fill: "rgba(0, 212, 255, 0.55)", fontSize: 10, fontFamily: "monospace" }}
              axisLine={false}
              tickLine={false}
              width={32}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fill: "rgba(0, 255, 157, 0.55)", fontSize: 10, fontFamily: "monospace" }}
              axisLine={false}
              tickLine={false}
              width={26}
            />
            <Tooltip
              cursor={{ stroke: "rgba(168, 85, 247, 0.3)", strokeWidth: 1 }}
              contentStyle={{
                background: "rgba(10, 10, 20, 0.92)",
                border: "1px solid rgba(168, 85, 247, 0.4)",
                borderRadius: 6,
                fontFamily: "monospace",
                fontSize: 12,
                boxShadow: "0 0 24px rgba(168, 85, 247, 0.25)",
              }}
              labelStyle={{ color: "rgba(240, 240, 255, 0.7)" }}
            />
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="impressions"
              stroke="url(#imprStroke)"
              strokeWidth={1.8}
              fill="url(#imprFill)"
              filter="url(#glowCyan)"
              isAnimationActive
              animationDuration={900}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="clicks"
              stroke="rgb(0 255 157)"
              strokeWidth={1.6}
              dot={false}
              activeDot={{ r: 3, fill: "rgb(0 255 157)", stroke: "rgba(0, 255, 157, 0.35)", strokeWidth: 6 }}
              filter="url(#glowGreen)"
              isAnimationActive
              animationDuration={900}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
